import React from 'react';
import { Card, Row, Col, Typography, Divider, Tag, Space } from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  IdcardOutlined,
  TeamOutlined,
  BankOutlined,
  ContactsOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const StaffProfile = ({ employee }) => {
  // Helper function to display field value or fallback
  const displayValue = (value, fallback = 'Not specified') => {
    return value && value.trim() !== '' ? value : fallback;
  };

  // Helper function to get appropriate icon for each field
  const getFieldIcon = (fieldType) => {
    const iconProps = { style: { color: '#a20', fontSize: '16px' } };
    
    switch (fieldType) {
      case 'employeeId':
        return <IdcardOutlined {...iconProps} />;
      case 'name':
        return <UserOutlined {...iconProps} />;
      case 'displayName':
        return <ContactsOutlined {...iconProps} />;
      case 'email':
        return <MailOutlined {...iconProps} />;
      case 'department':
        return <BankOutlined {...iconProps} />;
      case 'position':
        return <TeamOutlined {...iconProps} />;
      case 'phone':
        return <PhoneOutlined {...iconProps} />;
      default:
        return <UserOutlined {...iconProps} />;
    }
  };

  // Profile field component
  const ProfileField = ({ label, value, icon }) => (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '8px' 
      }}>
        {icon}
        <Text strong style={{ 
          marginLeft: '8px', 
          fontSize: '14px',
          color: '#595959'
        }}>
          {label}
        </Text>
      </div>
      <div style={{
        padding: '8px 12px',
        backgroundColor: '#fafafa',
        border: '1px solid #d9d9d9',
        borderRadius: '6px',
        minHeight: '32px',
        display: 'flex',
        alignItems: 'center'
      }}>
        <Text style={{ 
          fontSize: '14px',
          color: value === 'Not specified' ? '#8c8c8c' : '#262626'
        }}>
          {value}
        </Text>
      </div>
    </div>
  );

  if (!employee) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Text>No employee data available</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <Title level={3} style={{ marginBottom: '8px', color: '#a20' }}>
          Employee Profile
        </Title>
      </div>

      {/* Profile Information Card */}
      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
        bodyStyle={{ padding: '32px' }}
      >
        <Row gutter={[32, 0]}>
          {/* Left Column */}
          <Col xs={24} md={12}>
            <Title level={5} style={{ 
              marginBottom: '24px', 
              color: '#595959',
              borderBottom: '2px solid #f0f0f0',
              paddingBottom: '8px'
            }}>
              Personal Information
            </Title>
            
            <ProfileField
              label="Employee ID"
              value={displayValue(employee.employeeId)}
              icon={getFieldIcon('employeeId')}
            />
            
            <ProfileField
              label="Full Name"
              value={displayValue(employee.name)}
              icon={getFieldIcon('name')}
            />
            
            <ProfileField
              label="Display Name"
              value={displayValue(employee.displayName)}
              icon={getFieldIcon('displayName')}
            />
            
            <ProfileField
              label="Email Address"
              value={displayValue(employee.email)}
              icon={getFieldIcon('email')}
            />

            <ProfileField
              label="Phone Number"
              value={displayValue(employee.phoneNumber)}
              icon={getFieldIcon('phone')}
            />
          </Col>

          {/* Right Column */}
          <Col xs={24} md={12}>
            <Title level={5} style={{ 
              marginBottom: '24px', 
              color: '#595959',
              borderBottom: '2px solid #f0f0f0',
              paddingBottom: '8px'
            }}>
              Work Information
            </Title>
            
            <ProfileField
              label="Department"
              value={displayValue(employee.department)}
              icon={getFieldIcon('department')}
            />
            
            <ProfileField
              label="Position"
              value={displayValue(employee.position)}
              icon={getFieldIcon('position')}
            />

            {/* Account Status */}
            <div style={{ marginTop: '32px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '8px' 
              }}>
                <UserOutlined style={{ color: '#a20', fontSize: '16px' }} />
                <Text strong style={{ 
                  marginLeft: '8px', 
                  fontSize: '14px',
                  color: '#595959'
                }}>
                  Account Status
                </Text>
              </div>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#fafafa',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Tag color={employee.isActive !== false ? 'success' : 'error'}>
                  {employee.isActive !== false ? 'Active' : 'Inactive'}
                </Tag>
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Footer Note */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '24px',
        padding: '16px',
        backgroundColor: '#f6f8fa',
        borderRadius: '6px',
        border: '1px solid #e1e4e8'
      }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>
          To update your profile information, please contact your administrator.
        </Text>
      </div>
    </div>
  );
};

export default StaffProfile;
